import axios from "axios";
import apiClient from "@/core/api/apiClient";
import { ENDPOINTS } from "@/core/api/apiClient";
import UserModel, { UserUpdateRequest } from "./types";

/**
 * Fetches user profile data by email
 * @param email The user's email address
 */
export const fetchUserProfile = async (email: string): Promise<UserModel> => {
  try {
    console.log("Making API request to fetch profile for:", email);

    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();

    // Make API call with cache-busting parameters and headers
    // Check if the endpoint is defined before making the request
    if (!ENDPOINTS.GET_USER_DATA) {
      throw new Error("API endpoint for GET_USER_DATA is not defined");
    }

    const response = await apiClient.post(
      `${ENDPOINTS.GET_USER_DATA}?_t=${timestamp}`,
      { email },
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
          "If-None-Match": `${timestamp}`, // Ensure proper ETag handling
        },
      }
    );

    console.log("Profile API response:", response);

    if (response.status === 200 && response.data) {
      console.log("Received user data:", response.data);

      // Transform API response to UserModel
      const userData: UserModel = {
        email: response.data.email || email,
        firstName: response.data.firstName || "",
        lastName: response.data.lastName || "",
        phone: response.data.phone || "",
        birthDate:
          response.data.birthDate || new Date().toISOString().split("T")[0],
        isEmployee: Boolean(response.data.isEmployee),
        eventUpdates: Boolean(response.data.eventUpdates || false),
        promoOffers: Boolean(response.data.promoOffers || false),
        textMessages: Boolean(response.data.textMessages || false),
      };

      return userData;
    }

    throw new Error("Invalid response from server");
  } catch (error) {
    console.error("Error fetching profile:", error);

    if (axios.isAxiosError(error)) {
      console.error("API Error Details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    // Fallback to mock data in development environment
    if (process.env.NODE_ENV === "development") {
      console.log("Using mock profile data");

      // Update mock data to match what's in your DynamoDB table
      const mockData: UserModel = {
        email: email,
        firstName: "John",
        lastName: "Doe",
        phone: "+12029199109",
        birthDate: "1988-06-02", // Updated to match DynamoDB
        isEmployee: true, // Updated to match DynamoDB
        eventUpdates: true, // Updated to match DynamoDB
        promoOffers: true, // Updated to match DynamoDB
        textMessages: true, // Updated to match DynamoDB
      };

      return mockData;
    }

    throw new Error("Failed to fetch user profile data");
  }
};

/**
 * Updates user profile information
 * @param email User's email address (used as the identifier)
 * @param data The data to update
 */
export const updateUserProfile = async (
  email: string,
  data: UserUpdateRequest
): Promise<Partial<UserModel>> => {
  try {
    // Check if the endpoint is defined before making the request
    if (!ENDPOINTS.UPDATE_USER_DATA) {
      if (process.env.NODE_ENV === "development") {
        return data; // Return input data in dev mode if endpoint not defined
      }
      throw new Error("API endpoint for UPDATE_USER_DATA is not defined");
    }

    // Make actual API call to update user data
    const response = await apiClient.post(ENDPOINTS.UPDATE_USER_DATA, {
      email,
      ...data,
    });

    if (response.status === 200 && response.data) {
      // Return the updated data
      return response.data;
    }

    return data; // Fallback to returning the input data
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // In development, just simulate success
      return data;
    }
    throw new Error("Failed to update user profile");
  }
};

/**
 * Changes user's password
 * @param email User's email address
 * @param currentPassword Current password for verification
 * @param newPassword New password to set
 */
export const changePassword = async (
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<boolean> => {
  try {
    // Check if the endpoint is defined before making the request
    if (!ENDPOINTS.CHANGE_PASSWORD) {
      if (process.env.NODE_ENV === "development") {
        return true; // Simulate success in dev mode if endpoint not defined
      }
      throw new Error("API endpoint for CHANGE_PASSWORD is not defined");
    }

    // Make actual API call to change password
    const response = await apiClient.post(ENDPOINTS.CHANGE_PASSWORD, {
      email,
      currentPassword,
      newPassword,
    });

    return response.status === 200;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // In development, simulate success
      return true;
    }
    throw new Error("Failed to change password");
  }
};
